import Modal from "@/components/atoms/Modal";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import CompanyCourseInstructorInvoice, {
  CompanyCourseInstructorInvoiceForm,
} from "@/interfaces/CompanyCourseInstructorInvoice";
import { IModalChildrenProps, IModalMethods } from "@/interfaces/Modal";
import { useStoreCompanyCourseInstructorInvoiceMutation } from "@/services/companies/courses/instructors/invoices";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

interface StoreCompanyCourseInstructorInvoiceModalProps
  extends IModalChildrenProps<unknown, CompanyCourseInstructorInvoice> {
  companyId: string;
  courseId: string;
}

const StoreCompanyCourseInstructorInvoiceModal: ForwardRefRenderFunction<
  IModalMethods,
  StoreCompanyCourseInstructorInvoiceModalProps
> = ({ onHide, companyId, courseId }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [instructorId, setInstructorId] = useState<string>();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyCourseInstructorInvoiceForm>();
  const [storeCompanyCourseInstructorInvoiceAsync] =
    useStoreCompanyCourseInstructorInvoiceMutation();

  const showOrHiddenModal = (id?: string) => {
    setInstructorId(id);
    ModalRef.current?.showOrHiddenModal();
  };

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<CompanyCourseInstructorInvoiceForm> = (
    data
  ) => {
    if (!instructorId) throw new Error("INSTRUCTOR_ID_IS_REQUIRED");

    toast.promise(
      storeCompanyCourseInstructorInvoiceAsync([
        companyId,
        courseId,
        instructorId,
        data,
      ]).unwrap(),
      {
        pending: "Création du formateur en cours ...",
        success: {
          render() {
            showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                Le formateur à été créer 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  const onError: SubmitErrorHandler<CompanyCourseInstructorInvoiceForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Ajouter une facture"
      requiredLabel
      onHide={onHide}
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Numéro de facture <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : INV3837"
              {...register("invoiceNumber", {
                required: "Ce champ est obligatoire",
              })}
              isInvalid={!!errors.invoiceNumber}
            />
            <Form.Control.Feedback type="invalid">
              {errors.invoiceNumber?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nombre d'heures <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                placeholder="ex : 3"
                {...register("invoiceHoursNumber", {
                  required: "Ce champ est obligatoire",
                })}
                isInvalid={!!errors.invoiceHoursNumber}
              />
              <InputGroup.Text>heure(s)</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {errors.invoiceHoursNumber?.message}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Date de facture <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              {...register("invoiceDate", {
                required: "Ce champ est obligatoire",
              })}
              isInvalid={!!errors.invoiceDate}
            />
            <Form.Control.Feedback type="invalid">
              {errors.invoiceDate?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Montant de la facture <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup className="mb-2">
              <Form.Control
                type="number"
                placeholder="ex : 670.99"
                {...register("invoiceAmount", {
                  required: "Ce champ est obligatoire",
                })}
                isInvalid={!!errors.invoiceAmount}
              />
              <InputGroup.Text>€</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {errors.invoiceAmount?.message}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Date de paiement de la facture</Form.Label>
            <Form.Control
              type="date"
              {...register("invoicePaiementDate")}
              isInvalid={!!errors.invoicePaiementDate}
            />
            <Form.Control.Feedback type="invalid">
              {errors.invoicePaiementDate?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreCompanyCourseInstructorInvoiceModal);
